import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { WhatsappLogo, Warning, CalendarX, CheckSquare, Square } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTRUCTOR_PHONE = '31620933773';

const REASONS = [
    { id: 'ziek', label: 'Ziek', emoji: '🤒' },
    { id: 'examen', label: 'Examen / Toets', emoji: '📝' },
    { id: 'school', label: 'School', emoji: '🎓' },
    { id: 'werk', label: 'Werk', emoji: '💼' },
    { id: 'anders', label: 'Anders, namelijk...', emoji: '💬' },
];

function formatLessonLabel(l) {
    if (!l) return '';
    try {
        const d = new Date(`${l.date}T00:00:00`);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
        const target = new Date(d); target.setHours(0, 0, 0, 0);
        let datePart;
        if (target.getTime() === today.getTime()) datePart = 'Vandaag';
        else if (target.getTime() === tomorrow.getTime()) datePart = 'Morgen';
        else datePart = d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
        return `${datePart} ${l.time || ''} (${l.duration_minutes || 60} min)`;
    } catch {
        return `${l.date} ${l.time || ''}`;
    }
}

function hoursUntil(l) {
    if (!l?.date) return Infinity;
    const dt = new Date(`${l.date}T${l.time || '09:00'}`);
    return (dt - new Date()) / (1000 * 60 * 60);
}

/**
 * Bulk lesson-cancellation modal for students.
 * Loads all upcoming scheduled lessons, lets the student tick the ones they want
 * to cancel, pick a reason once, then logs each cancellation server-side and
 * opens a single WhatsApp message containing every selected lesson.
 */
export default function StudentCancelLessonsModal({ open, onClose, studentName }) {
    const [lessons, setLessons] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [selectedReason, setSelectedReason] = useState('');
    const [customReason, setCustomReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isValid = selected.size > 0
        && selectedReason
        && (selectedReason !== 'anders' || customReason.trim().length >= 3);

    useEffect(() => {
        if (!open) return;
        setSelected(new Set());
        setSelectedReason('');
        setCustomReason('');
        setLoading(true);
        axios.get(`${API}/scheduled-lessons`, { withCredentials: true })
            .then(({ data }) => {
                const now = new Date();
                const upcoming = (data || []).filter(l => {
                    try {
                        const dt = new Date(`${l.date}T${l.time || '00:00'}:00`);
                        return dt > now;
                    } catch { return false; }
                }).sort((a, b) =>
                    (a.date || '').localeCompare(b.date || '') ||
                    (a.time || '').localeCompare(b.time || '')
                );
                setLessons(upcoming);
                // Pre-select the closest upcoming lesson for convenience
                if (upcoming.length > 0) setSelected(new Set([upcoming[0].id]));
            })
            .catch(() => setLessons([]))
            .finally(() => setLoading(false));
    }, [open]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const reasonText = () => {
        if (selectedReason === 'anders') return customReason.trim();
        return REASONS.find(r => r.id === selectedReason)?.label || '';
    };

    const selectedLessons = lessons.filter(l => selected.has(l.id));
    const has24hWarning = selectedLessons.some(l => {
        const h = hoursUntil(l);
        return h >= 0 && h < 24;
    });

    const buildMessage = () => {
        const reason = reasonText();
        const lines = selectedLessons.map(l => `• ${l.date}${l.time ? ` om ${l.time}` : ''} (${l.duration_minutes || 60} min)${l.notes ? ` — ${l.notes}` : ''}`);
        const lessonsPart = lines.join('\n');
        const plural = selectedLessons.length > 1;
        return `Hallo,\n\nIk wil helaas ${plural ? 'de volgende rijlessen' : 'mijn rijles'} annuleren:\n\n${lessonsPart}\n\n👤 Naam: ${studentName}\n📌 Reden: ${reason}\n\nMijn excuses voor het ongemak. Ik hoor graag wanneer we ${plural ? 'nieuwe afspraken' : 'een nieuwe afspraak'} kunnen inplannen.\n\nMet vriendelijke groet,\n${studentName}`;
    };

    const handleSend = () => {
        if (!isValid || submitting) return;
        setSubmitting(true);

        // Open WhatsApp synchronously on click to avoid popup blockers
        const whatsappUrl = `https://wa.me/${INSTRUCTOR_PHONE}?text=${encodeURIComponent(buildMessage())}`;
        const win = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        if (!win || win.closed || typeof win.closed === 'undefined') {
            window.location.href = whatsappUrl;
        }

        // Fire-and-forget: log every selected lesson
        Promise.all(selectedLessons.map(l =>
            axios.post(`${API}/cancellations`, {
                scheduled_lesson_id: l.id,
                lesson_date: l.date,
                lesson_time: l.time,
                lesson_duration: l.duration_minutes,
                reason: reasonText(),
                reason_type: selectedReason,
            }, { withCredentials: true }).catch(e => console.error('Log cancel failed:', e))
        )).finally(() => {
            setSubmitting(false);
            onClose();
        });
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-[#2D3748] border-[#4A5568] text-white max-w-lg max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="student-cancel-modal">
                <DialogHeader className="px-4 sm:px-6 pt-5 pb-3 border-b border-[#4A5568] flex-shrink-0">
                    <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <CalendarX size={22} className="text-[#D4A537]" weight="fill" />
                        Lessen annuleren
                    </DialogTitle>
                    <DialogDescription className="text-gray-400 text-sm">
                        Selecteer welke lessen je wil annuleren. Je bericht wordt via WhatsApp naar de instructeur gestuurd.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                    {/* Lessons list */}
                    <div className="space-y-2">
                        <Label className="text-gray-300 text-sm">Komende lessen ({lessons.length})</Label>
                        {loading ? (
                            <div className="text-center text-sm text-gray-400 py-6">Lessen laden…</div>
                        ) : lessons.length === 0 ? (
                            <div className="text-center text-sm text-gray-400 py-6 border border-dashed border-[#4A5568] rounded-lg">
                                Je hebt geen komende lessen ingepland.
                            </div>
                        ) : (
                            lessons.map((l) => {
                                const checked = selected.has(l.id);
                                const within24 = hoursUntil(l) < 24 && hoursUntil(l) >= 0;
                                return (
                                    <button
                                        key={l.id}
                                        type="button"
                                        onClick={() => toggle(l.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
                                            checked
                                                ? 'border-[#D4A537] bg-[#D4A537]/15'
                                                : 'border-[#4A5568] bg-[#1A202C] hover:border-[#D4A537]/50'
                                        }`}
                                        data-testid={`cancel-select-${l.id}`}
                                    >
                                        {checked
                                            ? <CheckSquare size={20} weight="fill" className="text-[#D4A537] flex-shrink-0" />
                                            : <Square size={20} className="text-gray-500 flex-shrink-0" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {formatLessonLabel(l)}
                                            </div>
                                            {l.notes && (
                                                <div className="text-xs text-gray-400 truncate mt-0.5">{l.notes}</div>
                                            )}
                                        </div>
                                        {within24 && (
                                            <span className="text-[10px] uppercase tracking-wider bg-red-500/15 text-red-300 border border-red-400/40 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                                &lt;24u
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {has24hWarning && (
                        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 flex items-start gap-2" data-testid="bulk-24h-warning">
                            <Warning size={20} className="text-red-400 flex-shrink-0 mt-0.5" weight="fill" />
                            <div className="text-sm text-red-300">
                                <strong>Let op:</strong> één of meer geselecteerde lessen vallen binnen 24 uur. Deze worden meegerekend als gereden les volgens je lespakket.
                            </div>
                        </div>
                    )}

                    {/* Reason */}
                    {lessons.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-gray-300 text-sm">Reden van verhindering</Label>
                            <div className="grid grid-cols-1 gap-2">
                                {REASONS.map(r => (
                                    <button
                                        key={r.id}
                                        type="button"
                                        onClick={() => setSelectedReason(r.id)}
                                        data-testid={`cancel-reason-${r.id}`}
                                        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border text-left transition-all ${
                                            selectedReason === r.id
                                                ? 'bg-[#D4A537]/15 border-[#D4A537] text-white'
                                                : 'bg-[#1A202C] border-[#4A5568] text-gray-300 hover:border-[#D4A537]/50'
                                        }`}
                                    >
                                        <span className="text-xl">{r.emoji}</span>
                                        <span className="font-medium">{r.label}</span>
                                        {selectedReason === r.id && (
                                            <span className="ml-auto w-2 h-2 rounded-full bg-[#D4A537]" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {selectedReason === 'anders' && (
                        <div className="space-y-2">
                            <Label className="text-gray-300 text-sm">Beschrijf je reden</Label>
                            <Textarea
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                placeholder="Bijv. familieomstandigheden..."
                                className="bg-[#1A202C] border-[#4A5568] text-white min-h-[80px]"
                                data-testid="cancel-custom-reason"
                            />
                            {customReason.trim().length > 0 && customReason.trim().length < 3 && (
                                <p className="text-xs text-red-400">Voer minimaal 3 tekens in.</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="border-t border-[#4A5568] bg-[#2D3748] px-4 sm:px-6 py-3 flex flex-wrap justify-end gap-2 flex-shrink-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="border-[#4A5568] text-gray-300 hover:bg-[#3D4A5C]"
                        data-testid="bulk-cancel-close"
                    >
                        Terug
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!isValid || submitting}
                        className="bg-green-600 hover:bg-green-500 text-white font-semibold disabled:opacity-50"
                        data-testid="bulk-cancel-submit"
                    >
                        <WhatsappLogo size={18} className="mr-2" weight="fill" />
                        {submitting
                            ? 'Versturen...'
                            : selected.size > 1
                                ? `Stuur ${selected.size} annuleringen via WhatsApp`
                                : 'Stuur via WhatsApp'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
